/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";
import { connect } from "react-redux";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import {
  CollectionsBookmark,
  NoteAddRounded,
  ExpandMore,
  ExpandLess,
  Update,
} from "@material-ui/icons";
import { Fab, IconButton, Tooltip } from "@material-ui/core";

import { progressCircles } from "components/Form/FormField";
import { NoteEntry, NoteEntryType } from "./NoteEntry";
import { ClsrmActionType } from "./Classroom";
import { noteCtrlButtons } from "./NoteUXInfo";
import { VideoPlayer } from "./VideoPlayer";
import { EnrollDialog } from "client/views/CoursesPage/EnrollDialog";
import { Modal } from "components/Modal/Modal";

import {
  getNotes,
  updateNote,
  deleteNote,
  expandNotes,
  getLecsWithNotes,
  ApricotAction,
} from "client/actions/note";
import { NotesState, LecturesWithNotes } from "client/reducers/noteReducer";
import {
  selectorNotes,
  selectorLecsWithNotes,
} from "client/selectors/noteSelector";

import {
  sEnlargeNZ,
  sReduceNZ,
  sOpenNotes,
  sNotesCover2,
  sAddNote,
  sOverLimit,
  sTooManyNts,
} from "assets/jss/views/ResStrings";

import notesZoneStyle from "assets/jss/views/classroomPage/notesZoneStyle";
const styles = createStyles(notesZoneStyle as any);

import { theme } from "client/../helpers/mui";
import { idNewNtLarge, idOpenNotes } from "assets/jss/views/ElementIDs";

type NewEntryType = {
  mode: boolean;
  note: NoteEntryType | null;
};

export type EntryActionType = {
  action: "nz_update" | "nz_delete" | "nz_expanded" | "nz_endNewEntry" | "play";
  data?: any;
  newEntry?: boolean;
  noteId?: string;
  expanded?: boolean;
  vPos?: number;
};

interface Props extends WithStyles<typeof styles> {
  getNotes: ApricotAction;
  updateNote: ApricotAction;
  deleteNote: ApricotAction;
  expandNotes: ApricotAction;
  getLecsWithNotes: ApricotAction;
  notes: NotesState;
  lecsWithNotes: LecturesWithNotes;
  user: any;
  classes: any;
  // component parameters:
  cid: string;
  lid: string;
  owned: boolean;
  player: VideoPlayer;
  largeNZ: boolean;
  onAction: (params: ClsrmActionType) => void;
  portrait: boolean;
}

const NotesZoneBase: React.FC<Props> = ({
  getNotes,
  updateNote,
  deleteNote,
  expandNotes,
  getLecsWithNotes,
  notes,
  lecsWithNotes,
  user,
  classes,
  // component parameters:
  cid,
  lid,
  owned,
  player,
  largeNZ,
  onAction,
  portrait,
}: Props) => {
  // NotesZone may show three kinds of content:
  // 1. If notes of this lec is in Store cache, open and show the Notes.
  // 2. If there's no notes taken for this lec, open notes - it'll show "AddNote".
  // 3. Otherwise, show "Open Notes" button.

  // openNotesLec is the id of the lec. If it's not null, we open the Notes. Cases to open notes are:
  // 1. The notes is in reducer cache already.
  // 2. There's no notes taken. Open note will show the Add Note button
  const [openNotesLec, setOpenNotesLec] = useState<string | null>(null);

  // const [paper, setPaper] = useState("white");
  const [fetching, setFetching] = useState(false);
  const [newEntry, setNewEntry] = useState<NewEntryType>({
    mode: false,
    note: null,
  });
  const [showEnroll, setShowEnroll] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const notesLimit = 30;

  const notesColorLight = theme.palette.notes.light;
  const paper = "white";

  useEffect(() => {
    if (user) {
      getLecsWithNotes({ user: user._id, course: cid });
    }
  }, []);

  useEffect(() => {
    // when 1) lecture switch 2) orientation change, clear newEntry
    setNewEntry({ mode: false, note: null });
  }, [lid, portrait]);

  useEffect(() => {
    if (notes.lecQueue.indexOf(lid) !== -1) {
      // if notes of this lid is in cache, set openNotesLec to open notes
      setOpenNotesLec(lid);
      getNotes({ user: user._id, course: cid, lecture: lid });
    } else {
      // if notes of this lid not in cache
      setOpenNotesLec(
        // if no notes taken in this lecture, set openNotesLec to open notes - which shows AddNotes button
        lecsWithNotes && lecsWithNotes.lecList.indexOf(lid) === -1 ? lid : null,
      );
    }
  }, [lid, portrait, lecsWithNotes]);

  useEffect(() => {
    if (fetching) {
      setFetching(false);
    }
  }, [notes]);

  useEffect(() => {
    if (lid && openNotesLec === lid) {
      setFetching(true);
      getNotes({ user: user._id, course: cid, lecture: lid });
    }
  }, [openNotesLec, lid]);

  const openNotesHandler = (action: any) => {
    if (!user || !owned) {
      setShowEnroll(true);
    } else {
      action();
    }
  };

  const renderCoverButton = (params: any) => {
    return (
      <div className={classes.notesCover}>
        <div
          className={classes.notesCoverButton}
          onClick={() => openNotesHandler(params.action)}>
          <span id={params.id}>
            {params.icon}&nbsp;{params.title}
          </span>
          <div className={classes.notesCoverButton2}>
            {sNotesCover2}&nbsp;
            <Update />
          </div>
        </div>
        {showEnroll && (
          <EnrollDialog
            owned={owned}
            enroll={false}
            courses={[cid]}
            onExit={() => setShowEnroll(false)}
          />
        )}
      </div>
    );
  };

  const onNoteCtrl = (params: any) => {
    switch (params.action) {
      case "add":
        if (checkNotesLimit()) {
          setNewEntry({ mode: true, note: null });
        }
        break;

      case "unfold":
        // if there's any note is not expanded, expand all
        if (notes[lid].some((note: any) => !note.expanded))
          expandNotes({ lecture: lid, expanded: true });
        break;

      case "fold":
        // if there's any note is expanded, collpase all
        if (notes[lid].some((note: any) => note.expanded))
          expandNotes({ lecture: lid, expanded: false });
        break;

      case "enlarge":
        onAction({ action: "largeNZ", bool: true });
        break;

      case "reduce":
        onAction({ action: "largeNZ", bool: false });
        break;
    }
  };

  const renderNotesCtrl = () => {
    const addFab = (ctrl: any) => (
      <Tooltip key={ctrl.name} title={ctrl.name} arrow>
        <Fab
          id={ctrl.id}
          color="inherit"
          size="large"
          classes={{ colorInherit: classes.addButton }}
          onClick={() => ctrl.action()}>
          {ctrl.icon}
        </Fab>
      </Tooltip>
    );

    const ctrlButton = (ctrl: any, disabled: boolean) => (
      <Tooltip key={ctrl.name} title={ctrl.name} arrow>
        <span>
          <IconButton
            id={ctrl.id}
            color="primary"
            disabled={disabled}
            classes={{
              colorPrimary: classes.ctrlButton,
            }}
            onClick={() => ctrl.action()}>
            {ctrl.icon}
          </IconButton>
        </span>
      </Tooltip>
    );

    return (
      <>
        {noteCtrlButtons(onNoteCtrl, classes.ctrlIcon).map(
          (ctrl: any, idx: 0 | 1 | 2 | 3 | 4) => {
            switch (idx) {
              case 0: // Enlarge NZ
              case 1: // Reduce NZ
                if (portrait) return "";
                else
                  return (ctrl.name === sEnlargeNZ && !largeNZ) ||
                    (ctrl.name === sReduceNZ && largeNZ)
                    ? ctrlButton(ctrl, false)
                    : "";
              case 2: // Add new note
                return portrait
                  ? // for Portrait: disable in newEntry mode
                    ctrlButton(ctrl, newEntry.mode) // disable in newEntry mode
                  : // for Landscape: show when 1) not in newEntry; 2) notes exist (means "Add a Note" cover button isn't shown)
                    !newEntry.mode && notes[lid] && addFab(ctrl);
              case 3: // Expand all
              case 4: // Fold up all
                return ctrlButton(ctrl, !notes[lid] || newEntry.mode);
            }
          },
        )}
      </>
    );
  };

  const onEntryAction = (params: EntryActionType) => {
    switch (params.action) {
      case "nz_update": {
        const { data, newEntry } = params;
        updateNote(data);

        if (newEntry) {
          // keep the newEntry note
          setNewEntry((prev: any) => ({ ...prev, note: data.note }));
        }
        break;
      }

      case "nz_delete": {
        const { noteId, newEntry } = params;
        deleteNote({ lecture: lid, noteId });

        if (newEntry) {
          // clear the newEntry state
          setNewEntry({ mode: false, note: null });
        }
        break;
      }

      case "nz_expanded":
        expandNotes({
          lecture: lid,
          expanded: params.expanded,
          noteId: params.noteId,
        });
        break;

      case "nz_endNewEntry":
        setNewEntry({ mode: false, note: null });
        break;

      case "play": {
        const { action, vPos } = params;
        onAction({ action, num: vPos });
        break;
      }
    }
  };

  const currentVPos = () => {
    return player && player.isPlaying() ? player.getPosition() : -1;
  };

  const checkNotesLimit = () => {
    if (notes && notes[lid] && notes[lid].length >= notesLimit) {
      setShowAlert(true);
      return false;
    }
    return true;
  };

  const renderLecNotes = () => {
    if (lid !== notes.curLecture) return <></>;

    const lecNotes = notes[lid];
    // seq number for new entry (0 also means there's no note in this lecture)
    let seq =
      lecNotes && lecNotes.length ? lecNotes[lecNotes.length - 1].seq + 1 : 0;

    // seq should not be NaN. However, if an unknown bug makes seq a NaN, it'll cause NoteEntry never-ending write to
    // database. As a protection, we make sure seq is never NaN.
    if (seq === NaN) seq = 100;

    // Since there are cases NotesZone get re-rendered during newEntry mode (e.g., enlarge NotesZone), the NoteEntry of
    // newEntry may get unmounted/remounted. To be able to mount back the current new note, we keep a copy of new note in
    // newEntry.note.
    //
    // For the fresh new note, which we don't even have a copy, we use random() as the key - thus when we discard a new note,
    // we can re-mount a 'different' NoteEntry, without the leftover in NoteEntry.
    const newNoteEntry = (
      <NoteEntry
        key={newEntry.note ? newEntry.note._id : Math.random() * 100}
        newEntry={true}
        filter={{ user: user._id, course: cid, lecture: lid }}
        note={
          newEntry.note || {
            label: null,
            text: null,
            vpos: currentVPos(),
            expanded: true,
            seq,
          }
        }
        player={player}
        expandPane={true}
        entryAction={(params: any) => onEntryAction(params)}
        portrait={portrait}
      />
    );

    if (seq === 0) {
      return newEntry.mode
        ? newNoteEntry
        : renderCoverButton({
            action: () => setNewEntry({ mode: true, note: null }),
            icon: <NoteAddRounded />,
            title: sAddNote,
            id: idNewNtLarge,
          });
    } else {
      return (
        <>
          {newEntry.mode
            ? newNoteEntry
            : lecNotes.map((note: NoteEntryType) => {
                return (
                  <NoteEntry
                    expandPane={note.expanded}
                    key={note._id} // don't just use 'idx' of map, use something REALLY different like note._id
                    filter={{ user: user._id, course: cid, lecture: lid }}
                    note={note}
                    player={player}
                    entryAction={(params: any) => onEntryAction(params)}
                    portrait={portrait}
                  />
                );
              })}
        </>
      );
    }
  };

  const renderNotesPanel = () => {
    const notesColors = {
      white: ["#FFFAF2", "#000000"],
      // black: ["#0A0A0A", "#F0F0F0"],
    };

    // If there's no any note, or in newEntry mode, draw some grid lines in background for decoration
    const backgroundGrid = {
      backgroundSize: "40px 40px",
      backgroundImage: `linear-gradient(to right, #E0E0E0 1px, transparent 1px), linear-gradient(to bottom, ${notesColorLight} 1px, transparent 1px)`,
    };

    return (
      <div
        className={classes.notesPanel}
        style={{
          background: notesColors[paper][0],
          color: notesColors[paper][1],
          ...backgroundGrid,
        }}>
        {fetching ? (
          progressCircles()
        ) : (
          <>
            <div className={classes.notesList}>{renderLecNotes()}</div>
            <div className={classes.notesCtrl}>{renderNotesCtrl()}</div>
          </>
        )}
      </div>
    );
  };

  const nzResizeButton = () => {
    const iconStyle = { fontSize: "3rem", fontWeight: 600 };
    return (
      !portrait && (
        <Tooltip title={largeNZ ? sReduceNZ : sEnlargeNZ}>
          <Fab
            className={classes.nzResizeButton}
            onClick={() =>
              onNoteCtrl({ action: largeNZ ? "reduce" : "enlarge" })
            }>
            {largeNZ ? (
              <ExpandMore style={iconStyle} />
            ) : (
              <ExpandLess style={iconStyle} />
            )}
          </Fab>
        </Tooltip>
      )
    );
  };

  if (user && (!lecsWithNotes || lecsWithNotes.course !== cid)) {
    return progressCircles();
  }

  return openNotesLec && openNotesLec === lid ? (
    <>
      {showAlert && (
        <Modal
          title={sOverLimit}
          color={"warning"}
          onExit={() => setShowAlert(false)}>
          {sTooManyNts}
        </Modal>
      )}
      {/* When openNotesLec has value and matches lid, open the Notes */}
      {renderNotesPanel()}
    </>
  ) : (
    <>
      {renderCoverButton({
        action: () => setOpenNotesLec(lid),
        icon: <CollectionsBookmark />,
        title: sOpenNotes,
        id: idOpenNotes,
      })}
      {nzResizeButton()}
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    user: state.state_auth,
    notes: selectorNotes(state.state_notes),
    lecsWithNotes: selectorLecsWithNotes(state.state_notes),
  };
}

const base: any = NotesZoneBase;
export const NotesZone: any = withStyles(styles)(
  connect(mapStateToProps, {
    getNotes,
    updateNote,
    deleteNote,
    expandNotes,
    getLecsWithNotes,
  })(base),
);

export const NotesZoneTest = withStyles(styles)(NotesZoneBase);
